import Button from '@atlaskit/button/new';
import { FormHeader } from '@atlaskit/form';
import SectionMessage from '@atlaskit/section-message';
import Spinner from '@atlaskit/spinner';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from 'styled-components';

import config from 'appconfig';
import { Dimensions } from 'components/visuals';
import { fetchTaxRate } from 'modules/data/taxRates/actions';

import MACPageHeader from './components/macPageHeader';
import { getCountryByCountryName, getStateOptionFromStates } from './common/utils';
import {
  BillingDetails,
  ContactDetailsFormSection,
} from './components/contactDetails/MACContactDetailsFormSection';
import { defaultFormState } from './constants/constants';
import {
  selectContactsForOrganisation,
  selectCountriesByName,
  selectGetCustomerByEmail,
  selectSwitchedUser,
  getCurrentMACContactOrgCountry,
  selectCountryByName,
  selectValidateCountryChange,
  selectExternalProgress,
  selectCurrencyOnReconcilableValidation,
  selectExternal,
  selectCurrencyOnReconcilableValidationResult,
  selectTaxIdValidationResult,
  selectAddressValidationResult,
} from './duck/selectors';
import {
  fetchContactsForOrganisation,
  fetchExternal,
  fetchValidateCountryChange,
  validateAddressForContactDetailsAction,
  validateTaxIdForOrganisationDetailsAction,
} from './duck/actions/contact';
import { fetchCountryByName } from './duck/actions/country';
import { State } from '../../model/State';
import {
  checkIsCountryChile,
  checkIsCountryEgypt,
  checkIsCountryPhilippines,
  checkIsCountryThailand,
  RESTRICTED_COUNTRIES_ISO_CODES,
  RESTRICTED_EMAIL_DOMAINS,
  getTaxExemptions,
  getTaxExemptionNoByCountryState,
  checkIsCountrySerbia,
  checkIsCountryUkraine,
  checkIsCountryUzbekistan,
} from '../utils';
import { extractTLDFrom } from '../siteconfigurator/utils';
import { SelectOption } from './components/UpdatePaymentDetailsForm';
import t from 'modules/i18n/intl';
import { postReconcilableValidationResultAccounts } from './duck/actions/accountId';

const Section = styled.div`
  margin-bottom: ${Dimensions.units(5)}px;
  width: ${Dimensions.pageWidth / 2}px;

  button {
    margin-top: ${Dimensions.units(2)}px;
  }
`;

export const MACEditDetailsPage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const bannerElement = useRef(null);

  const userEmail = useSelector(selectSwitchedUser);
  const customerByEmail = useSelector((state) =>
    selectGetCustomerByEmail(state as State)?.[userEmail]?.ifSuccess((r) => r),
  );
  const customerPartnerId = customerByEmail?.organisationDetails?.id;
  const contactsForOrganisation = useSelector((state) =>
    selectContactsForOrganisation(state as State, customerPartnerId),
  );
  const currentContactOrgCountryISO = useSelector(getCurrentMACContactOrgCountry);
  const orgCountriesFromAccounts = useSelector(selectCountriesByName) || {};
  const allCountries = useSelector((state: State) => state.data.countries.all?.result);
  const externalProgress = useSelector(selectExternalProgress);
  const externalResult = useSelector(selectExternal);
  const currencyOnReconcilableValidationProgress = useSelector(
    selectCurrencyOnReconcilableValidation,
  );
  const currencyOnReconcilableValidationResult = useSelector(
    selectCurrencyOnReconcilableValidationResult,
  );
  const [isRuByRestricted, setIsRuByRestricted] = useState(false);
  const [billingDetailsForm, setBillingDetailsForm] = useState<BillingDetails>(defaultFormState);
  const [states, setStates] = useState<SelectOption[]>([]);
  const [countryOptions, setCountryOptions] = useState<SelectOption[]>([]);
  const [currencyChangeCountry, setCurrencyChangeCountry] = useState(false);
  const [hasReconcilableMessage, setHasReconcilableMessage] = useState(false);
  const [isCountrySelectChanged, setIsCountrySelectChanged] = useState(false);
  const [showSuccessMessage, setShowSuccessMassage] = useState(false);
  const [showErrorMessage, setShowErrorMassage] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const taxIdValidationResult = useSelector((state) =>
    selectTaxIdValidationResult(state as State, billingDetailsForm.taxId),
  );
  const addressValidationResult = useSelector((state) =>
    selectAddressValidationResult(
      state as State,
      billingDetailsForm?.address1 + billingDetailsForm?.address2,
    ),
  );
  const countryDetails = useSelector((state: State) =>
    selectCountryByName(state, billingDetailsForm.country.label),
  );
  const selectValidateCountry = useSelector((state: State) =>
    selectValidateCountryChange(state, billingDetailsForm.country.value),
  );

  useEffect(() => {
    document.title = intl.formatMessage({ id: 'mac.edit-details.title' });
  }, [
    userEmail,
    customerByEmail,
    customerPartnerId,
    contactsForOrganisation,
    currentContactOrgCountryISO,
    orgCountriesFromAccounts,
    allCountries,
    externalProgress,
    externalResult,
    currencyOnReconcilableValidationProgress,
    currencyOnReconcilableValidationResult,
  ]);

  useEffect(() => {
    if (customerByEmail) {
      dispatch(fetchContactsForOrganisation(customerPartnerId));
      const mainContactOrgCountry = customerByEmail?.organisationDetails?.country;

      if (mainContactOrgCountry) {
        dispatch(fetchCountryByName(mainContactOrgCountry));
      }
    }
  }, [customerByEmail]);

  useEffect(() => {
    const countryISOCodesFromAccounts = Object.values(orgCountriesFromAccounts).map(
      ({ _result }) => _result?.countryIsoCode,
    );

    const isISORestricted = [currentContactOrgCountryISO, ...countryISOCodesFromAccounts].some(
      (item) => RESTRICTED_COUNTRIES_ISO_CODES.includes(item),
    );

    const isEmailRestricted = RESTRICTED_EMAIL_DOMAINS.includes(extractTLDFrom(userEmail));

    if (isISORestricted || isEmailRestricted) {
      setIsRuByRestricted(true);
    } else {
      setIsRuByRestricted(false);
    }
  }, [currentContactOrgCountryISO, userEmail, orgCountriesFromAccounts]);

  useEffect(() => {
    if (billingDetailsForm.state.value) {
      dispatch(fetchTaxRate(billingDetailsForm.country.value, billingDetailsForm.state.value));
    }
  }, [billingDetailsForm.state.value]);

  useEffect(() => {
    if (customerByEmail && countryDetails && states.length && !isCountrySelectChanged) {
      setBillingDetailsForm({
        ...billingDetailsForm,
        state: getStateOptionFromStates(states, customerByEmail.organisationDetails.state),
      });
      dispatch(fetchValidateCountryChange(billingDetailsForm.country.value));
    }
  }, [customerByEmail, countryDetails, states.length, isCountrySelectChanged]);

  useEffect(() => {
    if (billingDetailsForm.country.label && isCountrySelectChanged) {
      dispatch(fetchCountryByName(billingDetailsForm.country.label));
    }
  }, [billingDetailsForm.country.value, isCountrySelectChanged]);

  useEffect(() => {
    if (selectValidateCountry?.irreconcilable?.length) {
      setCurrencyChangeCountry(true);
    } else {
      setCurrencyChangeCountry(false);
    }
  }, [selectValidateCountry?.irreconcilable?.length]);

  useEffect(() => {
    if (selectValidateCountry?.reconcilable?.length) {
      setHasReconcilableMessage(true);
    } else {
      setHasReconcilableMessage(false);
    }
  }, [selectValidateCountry?.reconcilable?.length]);

  useEffect(() => {
    if (allCountries?.length) {
      setCountryOptions(
        allCountries.map(({ countryIsoCode, displayName }) => ({
          label: displayName,
          value: countryIsoCode,
        })),
      );
    }
  }, [allCountries?.length]);

  useEffect(() => {
    if (countryDetails?.states.length && billingDetailsForm.country) {
      setStates(countryDetails.states.map(({ name, code }) => ({ label: name, value: code })));
    }
  }, [countryDetails, billingDetailsForm.country.value]);

  useEffect(() => {
    if (customerByEmail && allCountries) {
      const {
        contactDetails: { firstName, lastName, phone },
        organisationDetails: {
          name: companyName,
          address1,
          address2,
          city,
          country,
          postcode,
          taxId,
          payingVatResponsible: vatResponsible,
          vatStatus,
          organisationTaxExemptions,
          state,
        },
      } = customerByEmail;
      const countryObj = getCountryByCountryName(allCountries, country) || '';
      setBillingDetailsForm({
        firstName,
        lastName,
        phone,
        companyName,
        address1,
        address2,
        city,
        country: countryObj,
        state: { label: '', value: '' },
        postcode,
        taxId,
        vatResponsible,
        vatStatus: vatStatus ?? null,
        taxExemptions: getTaxExemptionNoByCountryState(
          countryObj.value,
          taxId,
          state,
          organisationTaxExemptions,
        ),
      });
    }
  }, [customerByEmail, allCountries]);

  useEffect(() => {
    const isInvalid = (validationResult) =>
      validationResult?.failed || validationResult?._result?.failureEntity;

    if (isSubmitted) {
      if (isInvalid(taxIdValidationResult) || isInvalid(addressValidationResult)) {
        setShowSuccessMassage(false);
        setShowErrorMassage(true);
      } else if (externalResult) {
        if (selectValidateCountry && currencyOnReconcilableValidationResult !== '') {
          setShowSuccessMassage(false);
          setShowErrorMassage(true);
        } else {
          setShowSuccessMassage(true);
          setShowErrorMassage(false);
        }
      } else if (externalProgress.failed) {
        setShowSuccessMassage(false);
        setShowErrorMassage(true);
      }
      if (bannerElement.current) {
        (bannerElement.current as Element).scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [
    externalResult,
    externalProgress,
    selectValidateCountry,
    currencyOnReconcilableValidationResult,
    taxIdValidationResult,
    addressValidationResult,
    isSubmitted,
  ]);

  const handleFormChange = (e, field) => {
    setBillingDetailsForm({
      ...billingDetailsForm,
      [field]: e.currentTarget.value,
    });
    setShowSuccessMassage(false);
    setShowErrorMassage(false);
    setIsSubmitted(false);
  };

  const handleFormCountrySelectChange = (e) => {
    setBillingDetailsForm({
      ...billingDetailsForm,
      country: e,
      state: { label: '', value: '' },
      taxId: '',
      vatResponsible: null,
      vatStatus: null,
      taxExemptions: '',
    });
    setIsCountrySelectChanged(true);
    dispatch(fetchValidateCountryChange(e.value));
    setShowSuccessMassage(false);
    setShowErrorMassage(false);
    setIsSubmitted(false);
  };

  const handleFormStateSelectChange = (e) => {
    setBillingDetailsForm({
      ...billingDetailsForm,
      state: e,
      taxId: '',
      taxExemptions: '',
    });
    setShowSuccessMassage(false);
    setShowErrorMassage(false);
    setIsSubmitted(false);
  };

  const showUpdateOrgDetailsWarning = contactsForOrganisation?.length > 1;
  const orgContactsToDisplay = contactsForOrganisation?.filter(
    (contact) => contact.contactDetails.email !== customerByEmail?.contactDetails?.email,
  );

  const currencyChangeCountryMessageDetails = {
    defaultMessage:
      'The change to {inputtedCountry} would result in a change of currency on your account. Please {contactUs} to change your billing country.',
    id: 'mac.edit-details.org.contact.irreconcilable.warning',
    values: {
      inputtedCountry: countryDetails?.displayName,
      contactUs: (
        <a href={config.companyContact} target="_blank" rel="noreferrer">
          <FormattedMessage id="common.contact-us" />
        </a>
      ),
    },
  };

  const getUpdatedContactDetails = () => ({
    contactDetails: {
      firstName: billingDetailsForm.firstName,
      lastName: billingDetailsForm.lastName,
      email: userEmail,
      phone: billingDetailsForm.phone,
    },
    organisationDetails: {
      name: billingDetailsForm.companyName,
      address1: billingDetailsForm.address1,
      address2: billingDetailsForm.address2,
      city: billingDetailsForm.city,
      state: billingDetailsForm.state.value,
      country: billingDetailsForm.country.label,
      postcode: billingDetailsForm.postcode,
      taxId: billingDetailsForm.taxId,
      payingVatResponsible: checkIsCountryUkraine(billingDetailsForm.country.value)
        ? false
        : billingDetailsForm.vatResponsible,
      vatStatus: checkIsCountryUkraine(billingDetailsForm.country.value)
        ? billingDetailsForm.vatStatus
        : null,
      organisationTaxExemptions: getTaxExemptions(
        billingDetailsForm.country.value,
        billingDetailsForm.taxId,
        billingDetailsForm.state.value,
        billingDetailsForm.taxExemptions,
      ),
    },
  });

  const handleFormSubmit = () => {
    if (isRuByRestricted) return;
    const updatedDetails = getUpdatedContactDetails();
    setIsSubmitted(true);
    dispatch(validateTaxIdForOrganisationDetailsAction(updatedDetails.organisationDetails));
    dispatch(validateAddressForContactDetailsAction(updatedDetails));
  };

  useEffect(() => {
    const isValid = (validationResult) =>
      validationResult?.success && !validationResult?._result?.failureEntity;

    if (isSubmitted && taxIdValidationResult?.isCompleted && addressValidationResult?.isCompleted) {
      if (isValid(taxIdValidationResult) && isValid(addressValidationResult)) {
        if (selectValidateCountry) {
          dispatch(postReconcilableValidationResultAccounts(selectValidateCountry));
        }
        dispatch(fetchExternal(getUpdatedContactDetails()));
      }
    }
  }, [taxIdValidationResult, addressValidationResult]);

  return (
    <>
      <MACPageHeader />
      {!customerByEmail || billingDetailsForm === defaultFormState ? (
        <Spinner size="large" />
      ) : (
        <>
          {(showSuccessMessage || showErrorMessage) && (
            <SectionMessage
              appearance={showSuccessMessage ? 'success' : 'error'}
              ref={bannerElement}
            >
              {showSuccessMessage
                ? 'Your details have been updated successfully!'
                : 'Something went wrong. Please try again.'}
            </SectionMessage>
          )}
          <Section data-cy="mac-billing-details-form">
            <FormHeader title={t('mac.edit-details.contact.title')} />
            <ContactDetailsFormSection
              countryDetails={countryDetails}
              billingDetailsForm={billingDetailsForm}
              states={states}
              countryOptions={countryOptions}
              currencyChangeCountry={currencyChangeCountry}
              currencyChangeCountryMessageDetails={currencyChangeCountryMessageDetails}
              hasReconcilableWarning={hasReconcilableMessage}
              handleFormChange={handleFormChange}
              handleFormStateSelectChange={handleFormStateSelectChange}
              handleFormCountrySelectChange={handleFormCountrySelectChange}
              setBillingDetailsForm={setBillingDetailsForm}
              isEditDetailsPage
              showUpdateOrgDetailsWarning={showUpdateOrgDetailsWarning}
              orgContactsToDisplay={orgContactsToDisplay}
            />
            <Button
              data-cy="edit-details-submit-button"
              appearance="primary"
              isLoading={
                externalProgress?.inProgress || currencyOnReconcilableValidationProgress?.inProgress
              }
              isDisabled={
                isRuByRestricted ||
                currencyChangeCountry ||
                !billingDetailsForm.firstName ||
                !billingDetailsForm.lastName ||
                !billingDetailsForm.companyName ||
                !billingDetailsForm.address1 ||
                !billingDetailsForm.city ||
                !billingDetailsForm.state.value ||
                !billingDetailsForm.country.value ||
                ((checkIsCountryChile(billingDetailsForm.country.value) ||
                  checkIsCountryPhilippines(billingDetailsForm.country.value) ||
                  checkIsCountryThailand(billingDetailsForm.country.value) ||
                  checkIsCountrySerbia(billingDetailsForm.country.value) ||
                  checkIsCountryUzbekistan(billingDetailsForm.country.value)) &&
                  !!billingDetailsForm.taxId &&
                  typeof billingDetailsForm.vatResponsible !== 'boolean') ||
                (checkIsCountryUkraine(billingDetailsForm.country.value) &&
                  !!billingDetailsForm.taxId &&
                  billingDetailsForm.vatStatus == null) ||
                (checkIsCountryEgypt(billingDetailsForm.country.value) &&
                  !!billingDetailsForm.taxId &&
                  !billingDetailsForm.taxExemptions)
              }
              onClick={handleFormSubmit}
            >
              <FormattedMessage
                defaultMessage="Save Details"
                id="mac.edit-details.contact.submit"
              />
            </Button>
          </Section>
        </>
      )}
    </>
  );
};
